/* @jsx h */
import { defineComponent, h, Fragment, computed } from "vue";
import { NavLink } from "./NavLink";
import { TrackedDesktopNav } from "./TrackedDesktopNav";
import { HelpMenu } from "./HelpMenu";

import { useAuth } from "@app/vue/providers/Auth";
import { useShop } from "@app/vue/providers/ShopConnection";
//
export const DesktopNav = defineComponent({
  name: "DesktopNav",
  props: {
    posMode: { type: Boolean, default: false },
  },
  setup(props) {
    const auth = useAuth();
    const { orders, pendingReservationRequestsCount } = useShop();

    const newOrdersCount = computed(() => {
      return orders.value.filter((order) => order.status === "new").length;
    });
    const pendingReservationRequestsBadge = computed(() => {
      return pendingReservationRequestsCount.value > 0
        ? pendingReservationRequestsCount.value
        : undefined;
    });

    const isProduction = import.meta.env.VITE_ENV === "production";
    // Work Day and Inventory are still being built: staging only.
    const isStaging = import.meta.env.VITE_ENV === "staging";

    const comparatorFn = (routePath: string, path: string) =>
      routePath.startsWith(path);
    const renderPOSNav = () => (
      <Fragment>
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/pos"
          icon="computer"
          title="nav.core.pos"
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/tables"
          icon="table_restaurant"
          title="nav.core.tables"
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/orders"
          icon="order_approve"
          title="nav.core.orders"
          badge={newOrdersCount.value > 0 ? newOrdersCount.value : undefined}
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/manager/reservations"
          icon="calendar_month"
          title="nav.labels.reservations"
          badge={pendingReservationRequestsBadge.value}
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/products"
          icon="fork_spoon"
          title="nav.core.products"
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/reports"
          icon="monitoring"
          title="nav.core.reports"
        />
        <HelpMenu />
      </Fragment>
    );

    const renderManagerNav = () => (
      <Fragment>
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/reports"
          icon="monitoring"
          title="manager.nav.dashboard"
        />
        {isProduction ? null : (
          <NavLink
            comparatorFn={comparatorFn}
            to="/app/intelligence"
            icon="auto_awesome"
            title="Agents"
          />
        )}

        <NavLink
          comparatorFn={comparatorFn}
          to="/app/manager/loyalty"
          icon="social_leaderboard"
          title="nav.labels.loyalty"
        />
        {isProduction ? (
          <NavLink
            comparatorFn={comparatorFn}
            to="/app/manager/reservations"
            icon="calendar_month"
            title="nav.labels.reservations"
            badge={pendingReservationRequestsBadge.value}
          />
        ) : null}

        <NavLink
          comparatorFn={comparatorFn}
          to="/app/tables"
          icon="table_restaurant"
          title="nav.core.tables"
        />
        {isProduction ? (
          <NavLink
            comparatorFn={comparatorFn}
            to="/manager/products"
            icon="category"
            title="manager.nav.catalog"
            external
          />
        ) : false ? (
          <NavLink
            comparatorFn={comparatorFn}
            to="/manager/products"
            icon="category"
            title="manager.nav.catalog"
            external
          />
        ) : (
          <NavLink
            comparatorFn={comparatorFn}
            to="/app/catalog_editor"
            icon="category"
            title="manager.nav.catalog"
          />
        )}
        {isStaging && (
          <NavLink
            comparatorFn={comparatorFn}
            to="/app/workday"
            icon="calendar_today"
            title="nav.labels.workday"
          />
        )}
        {isStaging && (
          <NavLink
            comparatorFn={comparatorFn}
            to="/app/inventory"
            icon="inventory"
            title="nav.labels.inventory"
          />
        )}

        <NavLink
          comparatorFn={comparatorFn}
          to="/app/settings"
          icon="settings"
          title="nav.labels.settings"
        />
        <HelpMenu />
      </Fragment>
    );

    const renderAdminNav = () => (
      <Fragment>
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/admin/dashboard"
          icon="dashboard"
          title="Dashboard"
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/admin/restaurants"
          icon="storefront"
          title="Shops"
        />
        <NavLink
          comparatorFn={comparatorFn}
          to="/app/admin/settings"
          icon="settings"
          title="Settings"
        />
      </Fragment>
    );

    return () => {
      if (!auth.user.value) return null;

      return (
        <TrackedDesktopNav
          posMode={props.posMode}
          renderItems={() => (
            <Fragment>
              {auth.user.value?.role === "staff" ? renderPOSNav() : null}
              {auth.user.value?.role === "manager"
                ? props.posMode
                  ? renderPOSNav()
                  : renderManagerNav()
                : null}
              {auth.user.value?.role === "admin" ? renderAdminNav() : null}
            </Fragment>
          )}
        />
      );
    };
  },
});
